import { Component, ReactNode, PropsWithChildren, ErrorInfo } from "react";

import { Helpers } from "../../analytics/atl-analytics/helpers";

export type ErrorBoundaryProps = PropsWithChildren<{
    fallback: ReactNode;
    onError?: (error: Error) => void;
    shouldShowFallback?: (error: Error) => boolean;
}>;

type State = {
    error?: Error;
};

export default class ErrorBoundary extends Component<
    ErrorBoundaryProps,
    State
> {
    public constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {};
    }

    private readonly promiseRejectionHandler = (
        event: PromiseRejectionEvent
    ) => {
        this.setState({ error: event.reason });
    };

    public static getDerivedStateFromError(error: Error) {
        // Update state so the next render will show the fallback UI.
        return { error };
    }

    public override componentDidCatch(error: Error, info: ErrorInfo) {
        Helpers.sendWrapperLoadError(
            error.message,
            info.componentStack ?? undefined
        );
    }

    public override componentDidMount() {
        // Add an event listener to the window to catch unhandled promise rejections & stash the error in the state
        window.addEventListener(
            "unhandledrejection",
            this.promiseRejectionHandler
        );
    }

    public override componentWillUnmount() {
        window.removeEventListener(
            "unhandledrejection",
            this.promiseRejectionHandler
        );
    }

    /** In the event that we only want to show a fallback for a specific error,
     * we can pass a function to shouldShowFallback that will determine if the fallback should be shown
     * Returns true if shouldShowFallback is not provided or if the function returns true
     **/
    private readonly shouldRenderFallback = () => {
        if (!this.state.error) {
            return false;
        }
        if (!this.props.shouldShowFallback) {
            return true;
        }

        return this.props.shouldShowFallback(this.state.error);
    };

    public override render() {
        const { onError, fallback, children } = this.props;
        const { error } = this.state;

        if (error) {
            onError && onError(error);
        }

        if (this.shouldRenderFallback()) {
            return fallback;
        }

        return children;
    }
}
